import styled from '@emotion/styled';
import { AdAnalyticsDailyPerformance } from '@kaizenplatform/hugot-entity';
import { List } from 'immutable';
import { createElement, FC } from 'react';

import DailyPerformanceGraph from './DailyPerformanceGraph';

type Props = {
  dailyPerformance: List<AdAnalyticsDailyPerformance>;
  className?: string;
};

type RawData = {
  key: string;
  date: string;
  value: number;
};

const DailyPerformanceList: FC<Props> = ({
  dailyPerformance,
  className,
}: {
  dailyPerformance: List<AdAnalyticsDailyPerformance>;
  className?: string;
}) => {
  const clicksData = dailyPerformance
    .map((dailyPerformance) =>
      dailyPerformance.clicks.map((click) => {
        return {
          key: dailyPerformance.adVideoId,
          date: click.date,
          value: click.actualValue.value,
        };
      }),
    )
    .toJS()
    .flat() as Array<RawData>;

  const impressionsData = dailyPerformance
    .map((dailyPerformance) =>
      dailyPerformance.impressions.map((impression) => {
        return {
          key: dailyPerformance.adVideoId,
          date: impression.date,
          value: impression.actualValue.value,
        };
      }),
    )
    .toJS()
    .flat() as Array<RawData>;

  const spendData = dailyPerformance
    .map((dailyPerformance) =>
      dailyPerformance.spend.map((spend) => {
        return {
          key: dailyPerformance.adVideoId,
          date: spend.date,
          value: spend.actualValue.value,
        };
      }),
    )
    .toJS()
    .flat() as Array<RawData>;

  // const mobileAppInstallsData: RawData[] = dailyPerformance
  //   .map(dailyPerformance =>
  //     dailyPerformance.mobileAppInstalls.map(mobileAppInstall => {
  //       return {
  //         key: dailyPerformance.adVideoId,
  //         date: mobileAppInstall.date,
  //         value: mobileAppInstall.actualValue.value,
  //       };
  //     }),
  //   )
  //   .toJS()
  //   .flat();

  const conversionsData = dailyPerformance
    .map((dailyPerformance) =>
      dailyPerformance.conversions.map((conversion) => {
        return {
          key: dailyPerformance.adVideoId,
          date: conversion.date,
          value: conversion.actualValue.value,
        };
      }),
    )
    .toJS()
    .flat() as Array<RawData>;

  const viewThroughConversionsData = dailyPerformance
    .map((dailyPerformance) =>
      dailyPerformance.viewThroughConversions.map((viewThroughConversion) => {
        return {
          key: dailyPerformance.adVideoId,
          date: viewThroughConversion.date,
          value: viewThroughConversion.actualValue.value,
        };
      }),
    )
    .toJS()
    .flat() as Array<RawData>;

  const ctrData = dailyPerformance
    .map((dailyPerformance) =>
      dailyPerformance.ctr.map((ctr) => {
        return {
          key: dailyPerformance.adVideoId,
          date: ctr.date,
          value: ctr.actualValue.value,
        };
      }),
    )
    .toJS()
    .flat() as Array<RawData>;

  const cpcData = dailyPerformance
    .map((dailyPerformance) =>
      dailyPerformance.cpc.map((cpc) => {
        return {
          key: dailyPerformance.adVideoId,
          date: cpc.date,
          value: Math.round(cpc.actualValue.value),
        };
      }),
    )
    .toJS()
    .flat() as Array<RawData>;

  const cpmData = dailyPerformance
    .map((dailyPerformance) =>
      dailyPerformance.cpm.map((cpm) => {
        return {
          key: dailyPerformance.adVideoId,
          date: cpm.date,
          value: Math.round(cpm.actualValue.value),
        };
      }),
    )
    .toJS()
    .flat() as Array<RawData>;

  const cpaData = dailyPerformance
    .map((dailyPerformance) =>
      dailyPerformance.cpa.map((cpa) => {
        return {
          key: dailyPerformance.adVideoId,
          date: cpa.date,
          value: Math.round(cpa.actualValue.value),
        };
      }),
    )
    .toJS()
    .flat() as Array<RawData>;

  const cpaWithViewThroughConversionsData = dailyPerformance
    .map((dailyPerformance) =>
      dailyPerformance.cpaWithViewThroughConversions.map((cpaWithViewThroughConversion) => {
        return {
          key: dailyPerformance.adVideoId,
          date: cpaWithViewThroughConversion.date,
          value: Math.round(cpaWithViewThroughConversion.actualValue.value),
        };
      }),
    )
    .toJS()
    .flat() as Array<RawData>;

  const formatData = (data: any) => {
    const result: any = [];
    for (let i = 0; i < data.length; i++) {
      const index = result.findIndex((res: any) => res.date === data[i].date);
      if (index !== -1) {
        result[index][data[i].key] = data[i].value;
      } else {
        result.push({
          date: data[i].date,
          [data[i].key]: data[i].value,
        });
      }
    }
    return result;
  };

  return (
    <Root className={className}>
      <DailyPerformanceGraph
        data={formatData(clicksData)}
        label="adAnalytics_list.anlytics_list_item.detail.column_name.click"
      />
      <DailyPerformanceGraph
        data={formatData(impressionsData)}
        label="adAnalytics_list.anlytics_list_item.detail.column_name.impression"
      />
      <DailyPerformanceGraph
        data={formatData(spendData)}
        label="adAnalytics_list.anlytics_list_item.detail.column_name.spend"
      />
      {/* <DailyPerformanceGraph
        data={formatData(mobileAppInstallsData)}
        label="adAnalytics_list.anlytics_list_item.detail.column_name.mobile_app_install"
      /> */}
      <DailyPerformanceGraph
        data={formatData(conversionsData)}
        label="adAnalytics_list.anlytics_list_item.detail.column_name.cv"
      />
      <DailyPerformanceGraph
        data={formatData(viewThroughConversionsData)}
        label="adAnalytics_list.anlytics_list_item.detail.column_name.vtcv"
      />
      <DailyPerformanceGraph
        data={formatData(ctrData)}
        label="adAnalytics_list.anlytics_list_item.detail.column_name.ctr"
      />
      <DailyPerformanceGraph
        data={formatData(cpcData)}
        label="adAnalytics_list.anlytics_list_item.detail.column_name.cpc"
      />
      <DailyPerformanceGraph
        data={formatData(cpmData)}
        label="adAnalytics_list.anlytics_list_item.detail.column_name.cpm"
      />
      <DailyPerformanceGraph
        data={formatData(cpaData)}
        label="adAnalytics_list.anlytics_list_item.detail.column_name.cpa"
      />
      <DailyPerformanceGraph
        data={formatData(cpaWithViewThroughConversionsData)}
        label="adAnalytics_list.anlytics_list_item.detail.column_name.cpavt"
      />
    </Root>
  );
};

const Root = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
});

export default DailyPerformanceList;
